import React, { useState } from 'react';

// --- IMAGE & ICON IMPORTS ---
import robPhoto from "@/assets/images/1.jpg";

const art1 = "https://images.unsplash.com/photo-1547826039-bfc35e0f1ea8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80";
const art2 = "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1045&q=80";
const art3 = "https://images.unsplash.com/photo-1501472312651-726afe119ff1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80";

import { Palette, BookOpen, PenTool, Users } from 'lucide-react';

// --- SELF-CONTAINED ANIMATIONS ---
const keyframes = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const About = () => {
    const [hoveredCard, setHoveredCard] = useState(null);
    const [hoveredImage, setHoveredImage] = useState(null);

    const styles = {
        main: {
            backgroundColor: 'var(--color-bg-card)', // Use a solid background from the theme
            fontFamily: "'Inter', sans-serif",
            color: 'var(--color-text-paragraph)', // Use theme's paragraph color
        },
        // --- Header Section ---
        header: {
            position: 'relative',
            height: '85vh',
            color: 'var(--color-text-heading)', // Use theme's heading color (for white/black)
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
        },
        headerBgImage: {
            position: 'absolute',
            inset: 0,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            backgroundImage: `url(${robPhoto})`,
            transform: 'scale(1.05)',
        },
        headerOverlay: {
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        headerContent: {
            position: 'relative',
            zIndex: 10,
            textAlign: 'center',
            padding: '2rem',
            animation: 'fadeInUp 1s ease-out 0.2s forwards',
            opacity: 0,
        },
        headerTitle: {
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
            fontWeight: 700,
            letterSpacing: '-0.05em',
            fontFamily: "'Playfair Display', serif",
            textShadow: '0 2px 15px rgba(0,0,0,0.5)',
        },
        headerSubtitle: {
            marginTop: '1rem',
            fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
            color: 'var(--color-text-default)', // Use theme's default light text color
            letterSpacing: '0.025em',
            fontWeight: 300,
            textShadow: '0 1px 10px rgba(0,0,0,0.5)',
        },
        // --- Section Styles ---
        section: {
            padding: '6rem 1rem',
        },
        sectionContainer: {
            maxWidth: '72rem',
            margin: '0 auto',
            padding: '0 1rem',
        },
        sectionHeader: {
            textAlign: 'left',
            marginBottom: '3rem',
        },
        sectionTitle: {
            fontSize: '2.5rem',
            fontWeight: 800,
            letterSpacing: '-0.025em',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--color-text-heading)', // Use theme's heading color
        },
        sectionSubtitle: {
            marginTop: '1rem',
            fontSize: '1.125rem',
            maxWidth: '48rem',
            lineHeight: 1.7,
            color: 'var(--color-text-paragraph)', // Use theme's paragraph color
        },
        // --- Creator Section ---
        creatorSection: { // Added for clarity
            backgroundColor: 'var(--color-bg-card)', // Match main bg or card bg
        },
        creatorContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4rem',
            alignItems: 'center',
        },
        creatorText: {
            flex: '1 1 400px',
        },
        creatorImageGrid: {
            flex: '1 1 400px',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: '1rem',
            aspectRatio: '1 / 1',
            minWidth: '300px',
        },
        creatorImage: {
            width: '100%',
            height: '100%',
            borderRadius: '0.5rem',
            objectFit: 'cover',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            cursor: 'pointer',
        },
        creatorImageHover: {
            transform: 'scale(1.05)',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
        },
        // --- Foundation Section ---
        foundationSection: {
            backgroundColor: 'var(--color-bg-page-gradient-to)', // Use the lighter page bg
            borderTop: '1px solid var(--content-border-color)', // Use theme border color
        },
        foundationGrid: {
            display: 'grid',
            gap: '2rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        },
        foundationCard: {
            padding: '2.5rem 2rem',
            backgroundColor: 'var(--color-bg-card)', // Use theme card background
            borderRadius: '0.75rem',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)',
            textAlign: 'center',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            cursor: 'pointer',
        },
        foundationCardHover: {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
        },
        foundationIconContainer: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '4rem',
            width: '4rem',
            borderRadius: '50%',
            marginBottom: '1.5rem',
            backgroundColor: 'var(--c-info-icon-bg, rgba(139, 0, 0, 0.1))', // Use theme variable with fallback
            transition: 'background-color 0.3s ease',
        },
        foundationTitle: {
            fontSize: '1.25rem',
            fontWeight: '600',
            color: 'var(--color-text-heading)', // Use theme heading color
        },
        foundationText: {
            marginTop: '0.5rem',
            color: 'var(--color-text-paragraph)', // Use theme paragraph color
            lineHeight: 1.6,
        },
    };
    
    return (
        <main role="main" style={styles.main}>
            <style>{keyframes}</style>
            
            <header style={styles.header}>
                <div style={styles.headerBgImage}></div>
                <div style={styles.headerOverlay}></div>
                <div style={styles.headerContent}>
                    <h1 style={styles.headerTitle}>Rob Granito</h1>
                    <p style={styles.headerSubtitle}>Illustrator & Visual Storyteller</p>
                </div>
            </header>

            <section id="creator" style={{...styles.section, ...styles.creatorSection}}>
                <div style={styles.sectionContainer}>
                    <div style={styles.creatorContainer}>
                        <div style={styles.creatorText}>
                            <div style={styles.sectionHeader}>
                                <h2 style={styles.sectionTitle}>
                                    <Palette style={{ height: '2.5rem', width: '2.5rem', marginRight: '1rem', color: 'var(--color-icon-accent)' }} />
                                    The Creator
                                </h2>
                            </div>
                            <p style={styles.sectionSubtitle}>
                                A multidisciplinary creator with a foundation in{" "}
                                <span style={{ fontWeight: 600, color: 'var(--color-text-heading)' }}>Illustration</span> and{" "}
                                <span style={{ fontWeight: 600, color: 'var(--color-text-heading)' }}>Communication Arts</span>. 
                                My work blends technical craft with strategic thinking to tell powerful stories that resonate and inspire.
                            </p>
                        </div>
                        <div style={styles.creatorImageGrid}>
                            <img src={art1} alt="Artwork 1" style={{ ...styles.creatorImage, gridRow: 'span 2', ...(hoveredImage === 'art1' && styles.creatorImageHover) }} onMouseEnter={() => setHoveredImage('art1')} onMouseLeave={() => setHoveredImage(null)} />
                            <img src={art2} alt="Artwork 2" style={{ ...styles.creatorImage, ...(hoveredImage === 'art2' && styles.creatorImageHover) }} onMouseEnter={() => setHoveredImage('art2')} onMouseLeave={() => setHoveredImage(null)} />
                            <img src={art3} alt="Artwork 3" style={{ ...styles.creatorImage, ...(hoveredImage === 'art3' && styles.creatorImageHover) }} onMouseEnter={() => setHoveredImage('art3')} onMouseLeave={() => setHoveredImage(null)} />
                        </div>
                    </div>
                </div>
            </section>
            
            <section id="foundation" style={{ ...styles.section, ...styles.foundationSection }}>
                <div style={styles.sectionContainer}>
                    <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
                        <h2 style={{ ...styles.sectionTitle, justifyContent: 'center' }}>My Creative Foundation</h2>
                        <p style={{ ...styles.sectionSubtitle, marginLeft: 'auto', marginRight: 'auto' }}>
                            Three core principles guide every project I undertake, ensuring work that is not only beautiful but also meaningful and effective.
                        </p>
                    </div>
                    <div style={styles.foundationGrid}>
                        <div style={{ ...styles.foundationCard, ...(hoveredCard === 'narrative' && styles.foundationCardHover) }} onMouseEnter={() => setHoveredCard('narrative')} onMouseLeave={() => setHoveredCard(null)}>
                            <div style={{...styles.foundationIconContainer, ...(hoveredCard === 'narrative' && {backgroundColor: 'var(--aa-image-border-accent, rgba(139, 0, 0, 0.2))'}) }}>
                                <BookOpen style={{ height: '2rem', width: '2rem', color: 'var(--color-icon-accent)' }} />
                            </div>
                            <h3 style={styles.foundationTitle}>Narrative Depth</h3>
                            <p style={styles.foundationText}>
                                Every piece begins with a story. I strive to create visuals that communicate complex ideas and evoke emotion, turning viewers into participants.
                            </p>
                        </div>
                        <div style={{ ...styles.foundationCard, ...(hoveredCard === 'mastery' && styles.foundationCardHover) }} onMouseEnter={() => setHoveredCard('mastery')} onMouseLeave={() => setHoveredCard(null)}>
                            <div style={{...styles.foundationIconContainer, ...(hoveredCard === 'mastery' && {backgroundColor: 'var(--aa-image-border-accent, rgba(139, 0, 0, 0.2))'})}}>
                                <PenTool style={{ height: '2rem', width: '2rem', color: 'var(--color-icon-accent)' }} />
                            </div>
                            <h3 style={styles.foundationTitle}>Technical Mastery</h3>
                            <p style={styles.foundationText}>
                                A commitment to craftsmanship is at the heart of my work. From digital pixels to traditional ink, precision and quality are paramount.
                            </p>
                        </div>
                        <div style={{ ...styles.foundationCard, ...(hoveredCard === 'collab' && styles.foundationCardHover) }} onMouseEnter={() => setHoveredCard('collab')} onMouseLeave={() => setHoveredCard(null)}>
                            <div style={{...styles.foundationIconContainer, ...(hoveredCard === 'collab' && {backgroundColor: 'var(--aa-image-border-accent, rgba(139, 0, 0, 0.2))'})}}>
                                <Users style={{ height: '2rem', width: '2rem', color: 'var(--color-icon-accent)' }} />
                            </div>
                            <h3 style={styles.foundationTitle}>Collaborative Spirit</h3>
                            <p style={styles.foundationText}>
                                The best results come from partnership. I work closely with clients to understand their vision and translate it into a compelling final product.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default About;